import React, {
  createContext,
  useCallback,
  useContext,
} from 'react';
import {
  List,
  TextField as RaTextField,
  useRecordContext,
  useSidebarState,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  CustomDatagrid,
  FilterField,
  FILTER_HEIGHT,
  ListActionsBar,
  NamedDeleteButton,
} from '@/components/common';
import { useCanCreate, useCanEdit, useCanDelete } from '@/utils/permissions';
import type { GroupRow } from '../services/groupService';

const i18nBase = 'resources.manage-groups';
const EDITOR_PATH = '/manage-groups/edit';

interface RowActionContextValue {
  onEdit: (row: GroupRow) => void;
}

const RowActionContext = createContext<RowActionContextValue | null>(null);
const useRowActions = (): RowActionContextValue => {
  const ctx = useContext(RowActionContext);
  if (!ctx) throw new Error('Row action used outside ManageGroupsList');
  return ctx;
};

const EditRowButton: React.FC = () => {
  const record = useRecordContext<GroupRow>();
  const translate = useTranslate();
  const { onEdit } = useRowActions();
  if (!record) return null;
  return (
    <Button
      size="small"
      variant="text"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        onEdit(record);
      }}
      sx={{ textTransform: 'none', fontWeight: 500, minWidth: 0 }}
    >
      {translate('ra.action.edit')}
    </Button>
  );
};

interface ListActionsProps {
  onAdd: () => void;
}

const ManageGroupsListActions: React.FC<ListActionsProps> = ({ onAdd }) => {
  const translate = useTranslate();
  const canCreate = useCanCreate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="groupName"
            label={translate(`${i18nBase}.filters.groupName`)}
          />
          <FilterField
            source="accountName"
            label={translate(`${i18nBase}.filters.accountName`)}
          />
          <FilterField
            source="applicationName"
            label={translate(`${i18nBase}.filters.applicationName`)}
          />
        </>
      }
      addButton={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
            startIcon={<AddIcon />}
            aria-label={translate(`${i18nBase}.actions.addGroup`)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              height: FILTER_HEIGHT,
              minWidth: { xs: FILTER_HEIGHT, sm: 'auto' },
              borderRadius: 1.5,
              px: { xs: 0, sm: 2.5 },
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 1 },
                ml: { xs: 0, sm: -0.5 },
              },
              '&:hover': { boxShadow: '0 2px 6px rgba(11, 165, 183, 0.25)' },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              {translate(`${i18nBase}.actions.addGroup`)}
            </Box>
          </Button>
        ) : null
      }
    />
  );
};

export const ManageGroupsList: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const maxWidth = isMobile ? '100%' : sidebarOpen ? '85vw' : '95vw';
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();

  const handleAdd = useCallback(() => navigate(EDITOR_PATH), [navigate]);

  const handleEdit = useCallback(
    (row: GroupRow) => {
      const qs = new URLSearchParams({ groupId: row.id }).toString();
      navigate(`${EDITOR_PATH}?${qs}`);
    },
    [navigate],
  );

  const handlers: RowActionContextValue = { onEdit: handleEdit };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.pageTitle`)}
        </Typography>
        <List
          actions={<ManageGroupsListActions onAdd={handleAdd} />}
          sort={{ field: '0', order: 'ASC' }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: 'visible',
            '& .RaList-main, & .RaList-content': {
              overflow: 'visible',
              width: '100%',
              maxWidth: '100%',
            },
            '& .RaListToolbar-root, & .RaList-actions': {
              display: 'flex',
              width: '100%',
              mb: 2,
              px: 0,
              py: { xs: 0.5, sm: 1 },
              minHeight: 'auto',
            },
          }}
        >
          <CustomDatagrid
            primarySource="groupName"
            secondarySource="accountName"
            bulkActionButtons={false}
            rowClick={false}
            disableMobileView
          >
            <RaTextField source="groupName" />
            <RaTextField source="accountName" />
            <RaTextField source="applicationName" />
            {canEdit && <EditRowButton />}
            {canDelete && <NamedDeleteButton displayField="groupName" />}
          </CustomDatagrid>
        </List>
      </Box>
    </RowActionContext.Provider>
  );
};

export default ManageGroupsList;
